/**
 * labels.{tenant} subdomain gate.
 *
 * The Label Manager panel is the upgraded-tier counterpart to the
 * basic creator panel. Only users on the `label_manager` account tier
 * (or admins for tooling/debug) should reach it; basic creators get
 * bounced to creator.{tenant}, sub-users to wherever their parent
 * account routes them.
 *
 * Pages on this subdomain reuse the /creator/* page tree (router.options.ts
 * does the path-prefix mapping); subdomain.global.ts swaps the layout
 * to `labels.vue` so the branding/sidebar reflect the LM context.
 *
 * Acceptance:
 *   role === 'admin'         (always — for support/debug access)
 *   role === 'label_manager' (the formalized upgraded tier; trusted
 *                             regardless of parentCreator — admins
 *                             can put a sub-user on this tier
 *                             explicitly via the user-edit UI)
 *   role === 'creator' + canCreateLabels=true + !parentCreator
 *                            (legacy fallback — only for standalone
 *                             accounts; sub-users with a stray
 *                             canCreateLabels flag stay on creator.*)
 *
 * Everything else (basic creators, sub-users without LM role,
 * customers) → bounced to creator.{tenant}.
 */
export default defineNuxtRouteMiddleware(() => {
    const url = useRequestURL()

    // Outside of labels.* this middleware is a no-op (it's only added
    // to the labels-subdomain auto-detection path in subdomain.global,
    // not declared on individual pages).
    if (!url.hostname.startsWith('labels.')) return

    const { user } = useAuth()
    if (!user.value) {
        // Unauthenticated → bounce to the auth page on the main domain.
        const mainDomain = url.hostname.replace('labels.', '')
        const protocol = url.protocol
        const port = url.port ? `:${url.port}` : ''
        return navigateTo(`${protocol}//${mainDomain}${port}/auth`, { external: true })
    }

    const role = user.value.role
    // Trust the explicit role. role='label_manager' → user belongs on
    // labels.* regardless of any legacy parentCreator value. The legacy
    // canCreateLabels fallback still requires !parentCreator so a basic
    // sub-user with an old plan flag doesn't sneak onto labels.*.
    const isLabelManager =
        role === 'label_manager'
        || (role === 'creator' && user.value.canCreateLabels === true && !user.value.parentCreator)

    if (role === 'admin' || isLabelManager) return

    // Sub-users without explicit label_manager role + basic creators
    // → bounced to creator.* (per-label grants flow through that panel).

    const mainDomain = url.hostname.replace('labels.', '')
    const protocol = url.protocol
    const port = url.port ? `:${url.port}` : ''

    // A&R scouts (role='ar') belong on scouts.{tenant}, not creator.*.
    // Without this special-case the scout would be bounced to
    // creator.{tenant} first and then scouts-redirect.global.ts would
    // bounce them again — wasted hop on every navigation that hits
    // labels.* by accident (bookmark, typed URL, stale post-login race).
    if (role === 'ar') {
        return navigateTo(`${protocol}//scouts.${mainDomain}${port}/`, { external: true })
    }

    // Basic creators (no LM privilege) → creator subdomain.
    return navigateTo(`${protocol}//creator.${mainDomain}${port}/`, { external: true })
})
